import "zod/compile";
import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  FORECAST_SETTING_KEYS,
  forecastSettingsSchema,
  planningLevelRulesSchema,
  forecastSettingsValuesSchema,
} from "@erp/shared-types";

/**
 * Org-wide forecast settings (`im_forecast_settings`, GEN-2699): the Demand
 * forecasting → General tab. One row per organization; no row means every
 * setting runs on the engine default. Changes are audited by the table's
 * `audit_change_log` trigger, which `history` reads back.
 */

export const ForecastSettingKeySchema = z.enum(FORECAST_SETTING_KEYS);

/** A setting's value as the audit log recorded it; null = not set. */
export const ForecastSettingAuditValueSchema = z.union([
  planningLevelRulesSchema,
  z.number(),
  z.record(z.string(), z.number()),
  z.null(),
]);

export const ForecastSettingsHistoryEntrySchema = z.object({
  id: z.string(),
  changedAt: z.string(),
  changedByUserId: z.string().uuid().nullable(),
  changedByUserName: z.string().nullable(),
  /** Only the settings that changed in this save, in display order. */
  changes: z.array(
    z.object({
      setting: ForecastSettingKeySchema,
      before: ForecastSettingAuditValueSchema,
      after: ForecastSettingAuditValueSchema,
    }),
  ),
});
export type ForecastSettingsHistoryEntry = z.infer<
  typeof ForecastSettingsHistoryEntrySchema
>;

export const ForecastSettingsResponseSchema = z.object({
  /** What the org saved; every field null when it never saved. */
  settings: forecastSettingsSchema,
  /** The engine's code defaults an unset setting falls back to. */
  defaults: forecastSettingsValuesSchema,
  /** What the next run uses: each saved setting, else its default. */
  effective: forecastSettingsValuesSchema,
  /** Optimistic-concurrency token for `update`; null before the first save. */
  revision: z.string().uuid().nullable(),
  updatedAt: z.string().nullable(),
  updatedByUserName: z.string().nullable(),
  /** Newest first. */
  history: z.array(ForecastSettingsHistoryEntrySchema),
});
export type ForecastSettingsResponse = z.infer<
  typeof ForecastSettingsResponseSchema
>;

export const UpdateForecastSettingsInputSchema = z.object({
  /** A full replace: a null field clears that setting back to its default. */
  settings: forecastSettingsSchema,
  /** The `revision` the form was loaded from, so a stale form cannot win. */
  expectedRevision: z.string().uuid().nullable(),
});
export type UpdateForecastSettingsInput = z.infer<
  typeof UpdateForecastSettingsInputSchema
>;

export const PlanningLevelRulesPreviewInputSchema = z.object({
  /** Null previews a reset to exact legacy behavior. */
  order: planningLevelRulesSchema.shape.order.nullable(),
});
const PreviewLevelsSchema = z.object({
  protection: z.number(),
  orderUpTo: z.number().nullable(),
  /** Flat-path trigger for forecast policies without a curve. */
  reorderPoint: z.number().nullable(),
  /** Flat-path fill target: never below that reorder point. */
  flatOrderUpTo: z.number().nullable(),
});
export const PlanningLevelRulesPreviewSchema = z.object({
  changedCount: z.number().int().nonnegative(),
  scannedCount: z.number().int().nonnegative(),
  scanLimit: z.number().int().positive(),
  truncated: z.boolean(),
  rows: z.array(
    z.object({
      policyId: z.string(),
      sku: z.string().nullable(),
      warehouseId: z.string(),
      warehouse: z.string().nullable(),
      before: PreviewLevelsSchema,
      after: PreviewLevelsSchema,
    }),
  ),
});
export type PlanningLevelRulesPreview = z.infer<
  typeof PlanningLevelRulesPreviewSchema
>;

export const forecastSettingsContracts = {
  previewPlanningLevelRules: oc
    .input(PlanningLevelRulesPreviewInputSchema)
    .output(PlanningLevelRulesPreviewSchema)
    .route({
      method: "GET",
      summary: "Preview planning level rule order changes",
      description:
        "Read-only. Lists the readable SKU×warehouse policies whose protection, order-up-to, or flat reorder point would change under a proposed rule order, against the org's current order. Scans at most 5,000 policies.",
    }),
  get: oc.input(z.object({})).output(ForecastSettingsResponseSchema).route({
    method: "GET",
    summary: "Get the organization's forecast settings",
    description:
      "Returns the org-wide demand forecast settings (history window, horizon, target availability by demand class and popularity code, lead-time fallback and unobserved lead-time guard), the engine defaults they fall back to, and their change history.",
  }),
  update: oc
    .input(UpdateForecastSettingsInputSchema)
    .output(ForecastSettingsResponseSchema)
    .route({
      method: "POST",
      summary: "Update the organization's forecast settings",
      description:
        "Replaces the org-wide demand forecast settings; a null setting falls back to the engine default. Applies from the next forecast run and to single-series recomputes.",
    }),
};
